import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { fetchWorkAuthorizationById, terminateWorkAuth } from '@/store/slices/workAuthSlice';
import { useToast } from '@/hooks/useToast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import DocumentReviewModal from '@/components/workauth/DocumentReviewModal';
import WorkAuthApprovalModal from '@/components/workauth/WorkAuthApprovalModal';
import IncomingEmailsCard from '@/components/workauth/IncomingEmailsCard';
import SmartsheetRecordCard from '@/components/workauth/SmartsheetRecordCard';
import {
  ArrowLeft,
  User,
  FileText,
  Calendar,
  Mail,
  Building,
  Clock,
  Download,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Bell,
  Upload,
  Eye,
  Check,
  ChevronDown
} from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import type { DocumentResponse } from '@shared/schemas/workAuth';
import { formatDate } from '@/utils/dateFormatter';

const WorkAuthDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { selectedWorkAuth, workAuthsLoading } = useAppSelector(state => state.workAuth);
  const { user } = useAppSelector(state => state.auth);

  // Modal state for document upload
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadDocType, setUploadDocType] = useState<string>('');
  const [uploading, setUploading] = useState(false);

  // Modal state for document review
  const [selectedDocument, setSelectedDocument] = useState<DocumentResponse | null>(null);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);

  // Modal state for work auth approval
  const [approvalModalOpen, setApprovalModalOpen] = useState(false);

  // Reminder state
  const [sendingReminder, setSendingReminder] = useState(false);

  // Collapsible panel state
  const [openPanels, setOpenPanels] = useState<Record<string, boolean>>({
    employee: true,
    workAuthDetails: true,
    smartsheet: true,
    timeline: true,
    documents: true,
    incomingEmails: true,
  });

  useEffect(() => {
    if (id) {
      dispatch(fetchWorkAuthorizationById(id));
    }
  }, [id, dispatch]);

  const handleTerminate = async () => {
    if (!id) return;
    if (confirm('Are you sure you want to terminate this work authorization?')) {
      await dispatch(terminateWorkAuth({ id, reason: 'Terminated by HR' }));
      navigate('/hr/work-auth/list');
    }
  };

  const handleApproveWorkAuth = async (data: { isPermanent: boolean; newExpirationDate?: Date; notes?: string }) => {
    if (!id) {
      throw new Error('Work authorization ID is missing');
    }

    console.log('Approving work auth with data:', data);

    try {
      // Call API directly instead of using Redux thunk to avoid triggering page-wide loading state
      const { workAuthService } = await import('@/services/workAuthService');
      const result = await workAuthService.approveWorkAuthorization(id, {
        isPermanent: data.isPermanent,
        newExpirationDate: data.newExpirationDate,
        notes: data.notes
      });

      console.log('Work auth approved successfully:', result);
      setApprovalModalOpen(false);

      // Refetch the work auth to update timeline and all details
      if (id) {
        dispatch(fetchWorkAuthorizationById(id));
      }
    } catch (error) {
      console.error('Failed to approve work authorization - caught error:', error);
      let errorMessage = 'Failed to approve work authorization';

      if (typeof error === 'string') {
        errorMessage = error;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'object' && error !== null && 'message' in error) {
        errorMessage = (error as any).message;
      }

      console.error('Throwing error with message:', errorMessage);
      throw new Error(errorMessage);
    }
  };

  const handleDownloadDocument = async (documentId: string) => {
    // TODO: Implement download
    console.log('Download document:', documentId);
  };

  const handleDocumentUpload = async () => {
    if (!uploadFile || !uploadDocType || !selectedWorkAuth || !user) return;

    setUploading(true);
    try {
      const { workAuthService } = await import('@/services/workAuthService');
      await workAuthService.uploadDocuments(
        selectedWorkAuth.id,
        uploadDocType,
        uploadFile,
        user.email
      );
      toast.success('Document uploaded', 'The document has been uploaded successfully.');
      setUploadDialogOpen(false);
      setUploadFile(null);
      setUploadDocType('');
      // Refresh work auth data to show new document
      dispatch(fetchWorkAuthorizationById(selectedWorkAuth.id));
    } catch (error: any) {
      console.error('Upload failed:', error);
      const errorMessage = error?.response?.data?.message || error.message || 'Failed to upload document';
      toast.error('Upload failed', errorMessage);
    } finally {
      setUploading(false);
    }
  };

  const handleSendReminder = async () => {
    if (!id || !selectedWorkAuth) return;

    try {
      setSendingReminder(true);
      const { workAuthService } = await import('@/services/workAuthService');
      const result = await workAuthService.triggerManualNotifications({
        workAuthId: id,
        forceResend: false
      });

      const daysLeft = getDaysUntilExpiration();
      toast.success(
        'Reminder Sent',
        `Notification sent to employee and manager (${daysLeft} days remaining)`
      );
    } catch (error: any) {
      console.error('Failed to send reminder:', error);
      const errorMessage = error?.response?.data?.message || error.message || 'Failed to send reminder';
      toast.error('Send Failed', errorMessage);
    } finally {
      setSendingReminder(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, { className: string; label: string }> = {
      ACTIVE: { className: 'bg-green-100 text-green-800', label: 'Active' },
      PENDING_RENEWAL: { className: 'bg-yellow-100 text-yellow-800', label: 'Pending Renewal' },
      DOCUMENTS_SUBMITTED: { className: 'bg-blue-100 text-blue-800', label: 'Documents Submitted' },
      APPROVED: { className: 'bg-green-100 text-green-800', label: 'Approved' },
      EXPIRED: { className: 'bg-red-100 text-red-800', label: 'Expired' },
      TERMINATED: { className: 'bg-gray-100 text-foreground', label: 'Terminated' }
    };
    const config = variants[status] || variants.ACTIVE;
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  const getReviewStatusIcon = (status: string) => {
    switch (status) {
      case 'APPROVED':
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'REJECTED':
        return <XCircle className="h-5 w-5 text-red-600" />;
      case 'PENDING':
        return <Clock className="h-5 w-5 text-yellow-600" />;
      default:
        return <Clock className="h-5 w-5 text-muted-foreground" />;
    }
  };

  const isPermanentApproval = () => {
    if (!selectedWorkAuth) return false;
    // Permanent approvals have expiration date of 9999-12-31
    const expirationYear = new Date(selectedWorkAuth.expirationDate).getFullYear();
    return expirationYear === 9999;
  };

  const getDaysUntilExpiration = () => {
    if (!selectedWorkAuth || isPermanentApproval()) return 0;
    const today = new Date();
    const expiration = new Date(selectedWorkAuth.expirationDate);
    const diffTime = expiration.getTime() - today.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const hasApprovedDocuments = () => {
    if (!selectedWorkAuth || !selectedWorkAuth.documents) return false;
    return selectedWorkAuth.documents.some((doc: any) => doc.reviewStatus === 'APPROVED');
  };

  if (workAuthsLoading || !selectedWorkAuth) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const employee = selectedWorkAuth.employee;
  const daysLeft = getDaysUntilExpiration();

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" onClick={() => navigate('/hr/work-auth/list')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to List
          </Button>
          <div>
            <h1 className="text-3xl font-bold">
              {employee?.firstName} {employee?.lastName}
            </h1>
            <p className="text-muted-foreground mt-1">Work Authorization Details</p>
          </div>
        </div>
        <Button variant="destructive" onClick={handleTerminate}>
          <AlertTriangle className="h-4 w-4 mr-2" />
          Terminate
        </Button>
      </div>

      {/* Expiration Alert - only show for temporary approvals */}
      {!isPermanentApproval() && daysLeft <= 30 && daysLeft > 0 && (
        <div className="bg-red-600 border-2 border-red-700 rounded-lg p-4 shadow-lg">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-6 w-6 text-white flex-shrink-0" />
            <div>
              <p className="text-white font-bold text-lg">Urgent Expiration Notice</p>
              <p className="text-white mt-1">This work authorization expires in <span className="font-bold text-xl">{daysLeft} days</span></p>
              <p className="text-red-100 text-sm mt-1">Renewal action required immediately</p>
            </div>
          </div>
        </div>
      )}

      {!isPermanentApproval() && daysLeft <= 0 && (
        <div className="bg-red-700 border-2 border-red-900 rounded-lg p-4 shadow-lg">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-6 w-6 text-white flex-shrink-0" />
            <div>
              <p className="text-white font-bold text-lg">Work Authorization Expired</p>
              <p className="text-white mt-1">This work authorization has <span className="font-bold">expired</span></p>
              <p className="text-red-100 text-sm mt-1">Employee cannot continue work - immediate action required</p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Employee & Work Auth Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Employee Information */}
          <Collapsible open={openPanels.employee} onOpenChange={(open) => setOpenPanels(prev => ({ ...prev, employee: open }))}>
            <Card>
              <CollapsibleTrigger asChild>
                <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                  <CardTitle className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <User className="h-5 w-5" />
                      Employee Information
                    </span>
                    <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${openPanels.employee ? '' : '-rotate-90'}`} />
                  </CardTitle>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Full Name</p>
                      <p className="font-medium">
                        {employee?.firstName} {employee?.lastName}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Employee ID</p>
                      <p className="font-medium">{employee?.employeeId}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Email</p>
                      <p className="font-medium">{employee?.email}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Manager Email</p>
                      <p className="font-medium">{employee?.managerEmail || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Employee Type</p>
                      <p className="font-medium">{employee?.employeeType}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Position</p>
                      <p className="font-medium">{employee?.position || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Department</p>
                      <p className="font-medium">{employee?.departmentId || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Hire Date</p>
                      <p className="font-medium">
                        {employee?.hireDate ? new Date(employee.hireDate).toLocaleDateString() : 'N/A'}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>

          {/* Work Authorization Details */}
          <Collapsible open={openPanels.workAuthDetails} onOpenChange={(open) => setOpenPanels(prev => ({ ...prev, workAuthDetails: open }))}>
            <Card>
              <CollapsibleTrigger asChild>
                <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                  <CardTitle className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <FileText className="h-5 w-5" />
                      Work Authorization Details
                    </span>
                    <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${openPanels.workAuthDetails ? '' : '-rotate-90'}`} />
                  </CardTitle>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Document Type</p>
                      <p className="font-medium">{selectedWorkAuth.documentType}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Current Status</p>
                      <div>{getStatusBadge(selectedWorkAuth.currentStatus)}</div>
                    </div>
                    {isPermanentApproval() ? (
                      <div className="col-span-2">
                        <p className="text-sm text-muted-foreground">Approval Status</p>
                        <div className="flex items-center gap-2">
                          <CheckCircle className="h-5 w-5 text-green-600" />
                          <p className="font-medium text-green-600">Permanent Approval</p>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div>
                          <p className="text-sm text-muted-foreground">Expiration Date</p>
                          <p className="font-medium">
                            {new Date(selectedWorkAuth.expirationDate).toLocaleDateString()}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Days Until Expiration</p>
                          <p className={`font-medium ${daysLeft <= 30 ? 'text-red-600' : daysLeft <= 60 ? 'text-yellow-600' : ''}`}>
                            {daysLeft > 0 ? `${daysLeft} days` : 'Expired'}
                          </p>
                        </div>
                      </>
                    )}
                    <div>
                      <p className="text-sm text-muted-foreground">Created At</p>
                      <p className="font-medium">
                        {new Date(selectedWorkAuth.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Last Updated</p>
                      <p className="font-medium">
                        {new Date(selectedWorkAuth.updatedAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>

          {/* Smartsheet Record */}
          <SmartsheetRecordCard
            workAuthId={id!}
            isOpen={openPanels.smartsheet}
            onToggle={() => setOpenPanels(prev => ({ ...prev, smartsheet: !prev.smartsheet }))}
          />

          {/* Renewal Cycle Timeline - Hidden for permanently approved */}
          {!isPermanentApproval() && selectedWorkAuth.renewalCycles && selectedWorkAuth.renewalCycles.filter((c: any) => c.isActive).length > 0 && (() => {
            const activeCycle = selectedWorkAuth.renewalCycles.find((c: any) => c.isActive);
            if (!activeCycle) return null;

            const expirationDate = new Date(selectedWorkAuth.expirationDate);
            const today = new Date();

            // Calculate milestone dates (90, 60, 30 days before expiration)
            const day90 = new Date(expirationDate);
            day90.setDate(day90.getDate() - 90);

            const day60 = new Date(expirationDate);
            day60.setDate(day60.getDate() - 60);

            const day30 = new Date(expirationDate);
            day30.setDate(day30.getDate() - 30);

            // Calculate timeline position (0-100%)
            const getTimelinePosition = (date: Date) => {
              const totalDuration = expirationDate.getTime() - day90.getTime();
              const elapsed = date.getTime() - day90.getTime();
              const position = (elapsed / totalDuration) * 100;
              return Math.max(0, Math.min(100, position));
            };

            // Collect all events from notifications and documents
            const events: Array<{
              date: Date;
              type: string;
              description: string;
              icon: React.ReactNode;
              color: string;
            }> = [];

            // Add notification events
            if (selectedWorkAuth.notifications) {
              selectedWorkAuth.notifications.forEach((notif: any) => {
                const notifDate = new Date(notif.sentAt);
                if (notifDate >= day90 && notifDate <= expirationDate) {
                  let description = '';
                  let icon = <Bell className="h-3 w-3" />;

                  if (notif.recipientType === 'MANAGER') {
                    description = `Manager notified (${notif.notificationType})`;
                  } else if (notif.recipientType === 'EMPLOYEE') {
                    description = `Employee notified (${notif.notificationType})`;
                  } else {
                    description = `HR notified (${notif.notificationType})`;
                  }

                  events.push({
                    date: notifDate,
                    type: 'notification',
                    description,
                    icon,
                    color: 'bg-blue-500'
                  });
                }
              });
            }

            // Add document events
            if (selectedWorkAuth.documents) {
              selectedWorkAuth.documents.forEach((doc: any) => {
                const submitDate = new Date(doc.submittedAt);
                if (submitDate >= day90 && submitDate <= expirationDate) {
                  events.push({
                    date: submitDate,
                    type: 'document',
                    description: `Document submitted`,
                    icon: <Upload className="h-3 w-3" />,
                    color: 'bg-purple-500'
                  });
                }

                if (doc.reviewedAt) {
                  const reviewDate = new Date(doc.reviewedAt);
                  if (reviewDate >= day90 && reviewDate <= expirationDate) {
                    const reviewColor = doc.reviewStatus === 'APPROVED' ? 'bg-green-500' :
                                       doc.reviewStatus === 'REJECTED' ? 'bg-red-500' : 'bg-yellow-500';
                    events.push({
                      date: reviewDate,
                      type: 'review',
                      description: `Document ${doc.reviewStatus.toLowerCase()}`,
                      icon: <Eye className="h-3 w-3" />,
                      color: reviewColor
                    });
                  }
                }
              });
            }

            // Sort events by date
            events.sort((a, b) => a.date.getTime() - b.date.getTime());

            return (
              <Collapsible open={openPanels.timeline} onOpenChange={(open) => setOpenPanels(prev => ({ ...prev, timeline: open }))}>
                <Card>
                  <CollapsibleTrigger asChild>
                    <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                      <CardTitle className="flex items-center justify-between">
                        <span className="flex items-center gap-2">
                          <Clock className="h-5 w-5" />
                          Renewal Cycle Timeline
                        </span>
                        <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${openPanels.timeline ? '' : '-rotate-90'}`} />
                      </CardTitle>
                      <CardDescription>
                        Current renewal cycle progress and events
                      </CardDescription>
                    </CardHeader>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <CardContent>
                  <div className="relative pb-8">
                    {/* Timeline line */}
                    <div className="absolute top-8 left-0 w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full"></div>

                    {/* Progress overlay */}
                    {today >= day90 && today <= expirationDate && (
                      <div
                        className="absolute top-8 left-0 h-2 bg-gradient-to-r from-teal-400 to-red-500 rounded-full transition-all duration-500"
                        style={{ width: `${getTimelinePosition(today)}%` }}
                      ></div>
                    )}

                    {/* Milestone markers */}
                    <div className="relative flex justify-between text-xs mb-12">
                      {/* 90 Days */}
                      <div className="absolute left-0 -top-2">
                        <div className="flex flex-col items-center">
                          <div className="w-1 h-6 bg-teal-500 mb-1"></div>
                          <div className="bg-teal-500 text-white px-3 py-1 rounded font-semibold whitespace-nowrap">
                            90d
                          </div>
                          <div className="text-xs text-muted-foreground mt-1">
                            {day90.toLocaleDateString()}
                          </div>
                        </div>
                      </div>

                      {/* 60 Days */}
                      <div className="absolute left-1/3 -top-2 transform -translate-x-1/2">
                        <div className="flex flex-col items-center">
                          <div className="w-1 h-6 bg-yellow-500 mb-1"></div>
                          <div className="bg-yellow-500 text-white px-3 py-1 rounded font-semibold whitespace-nowrap">
                            60d
                          </div>
                          <div className="text-xs text-muted-foreground mt-1">
                            {day60.toLocaleDateString()}
                          </div>
                        </div>
                      </div>

                      {/* 30 Days */}
                      <div className="absolute left-2/3 -top-2 transform -translate-x-1/2">
                        <div className="flex flex-col items-center">
                          <div className="w-1 h-6 bg-orange-500 mb-1"></div>
                          <div className="bg-orange-500 text-white px-3 py-1 rounded font-semibold whitespace-nowrap">
                            30d
                          </div>
                          <div className="text-xs text-muted-foreground mt-1">
                            {day30.toLocaleDateString()}
                          </div>
                        </div>
                      </div>

                      {/* Expiration */}
                      <div className="absolute right-0 -top-2">
                        <div className="flex flex-col items-center">
                          <div className="w-1 h-6 bg-red-500 mb-1"></div>
                          <div className="bg-red-500 text-white px-3 py-1 rounded font-semibold whitespace-nowrap">
                            Expiry
                          </div>
                          <div className="text-xs text-muted-foreground mt-1">
                            {expirationDate.toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Event markers */}
                    <div className="relative mt-16">
                      {events.map((event, idx) => {
                        const position = getTimelinePosition(event.date);
                        return (
                          <div
                            key={idx}
                            className="absolute group"
                            style={{ left: `${position}%`, top: '-60px' }}
                          >
                            <div className={`w-6 h-6 ${event.color} rounded-full flex items-center justify-center text-white shadow-lg cursor-pointer transform -translate-x-1/2`}>
                              {event.icon}
                            </div>

                            {/* Tooltip */}
                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                              <div className="bg-gray-900 text-white text-xs rounded-lg p-2 shadow-lg whitespace-nowrap">
                                <div className="font-semibold">{event.description}</div>
                                <div className="text-gray-300 mt-0.5">{event.date.toLocaleDateString()}</div>
                              </div>
                            </div>
                          </div>
                        );
                      })}

                      {/* Today marker */}
                      {today >= day90 && today <= expirationDate && (
                        <div
                          className="absolute"
                          style={{ left: `${getTimelinePosition(today)}%`, top: '-60px' }}
                        >
                          <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white shadow-xl transform -translate-x-1/2 border-4 border-white dark:border-gray-800">
                            <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                          </div>
                          <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 text-xs font-bold text-blue-600 whitespace-nowrap">
                            Today
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Events list below timeline */}
                  {events.length > 0 && (
                    <div className="mt-12 pt-4 border-t">
                      <h4 className="text-sm font-semibold mb-3">Event History</h4>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {events.map((event, idx) => (
                          <div key={idx} className="flex items-center gap-3 text-sm">
                            <div className={`w-4 h-4 ${event.color} rounded-full flex items-center justify-center text-white flex-shrink-0`}>
                              {event.icon}
                            </div>
                            <span className="text-muted-foreground">{event.date.toLocaleDateString()}</span>
                            <span>-</span>
                            <span className="font-medium">{event.description}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                    </CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible>
            );
          })()}

          {/* Documents */}
          <Collapsible open={openPanels.documents} onOpenChange={(open) => setOpenPanels(prev => ({ ...prev, documents: open }))}>
            <Card>
              <CollapsibleTrigger asChild>
                <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <FileText className="h-5 w-5" />
                        Uploaded Documents
                        <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${openPanels.documents ? '' : '-rotate-90'}`} />
                      </CardTitle>
                      <CardDescription className="mt-1">Documents submitted for this work authorization</CardDescription>
                    </div>
                    <div onClick={(e) => e.stopPropagation()}>
                      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
                        <DialogTrigger asChild>
                          <Button size="sm">
                            <Upload className="h-4 w-4 mr-2" />
                            Upload Document
                          </Button>
                        </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Upload Document</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label htmlFor="documentType">Document Type</Label>
                        <Select value={uploadDocType} onValueChange={setUploadDocType}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select document type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="I9_FORM">I-9 Form</SelectItem>
                            <SelectItem value="WORK_AUTHORIZATION">Work Authorization</SelectItem>
                            <SelectItem value="PASSPORT">Passport</SelectItem>
                            <SelectItem value="OTHER">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="file">File</Label>
                        <input
                          id="file"
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                          onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                          className="block w-full text-sm text-muted-foreground
                            file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0
                            file:text-sm file:font-medium file:bg-primary file:text-primary-foreground
                            hover:file:bg-primary/90 cursor-pointer"
                        />
                        {uploadFile && (
                          <p className="text-sm text-muted-foreground">
                            Selected: {uploadFile.name} ({(uploadFile.size / 1024).toFixed(1)} KB)
                          </p>
                        )}
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setUploadDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button onClick={handleDocumentUpload} disabled={!uploadFile || !uploadDocType || uploading}>
                        {uploading ? 'Uploading...' : 'Upload'}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                  </Dialog>
                    </div>
                  </div>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent>
              {selectedWorkAuth.documents && selectedWorkAuth.documents.length > 0 ? (
                <div className="space-y-3">
                  {selectedWorkAuth.documents.map((doc: any) => (
                    <div key={doc.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center gap-3">
                        {getReviewStatusIcon(doc.reviewStatus)}
                        <div>
                          <p className="font-medium">{doc.fileName}</p>
                          <p className="text-sm text-muted-foreground">
                            Uploaded {formatDate(doc.submittedAt)} by {doc.submittedBy}
                          </p>
                          {doc.reviewStatus === 'APPROVED' ? (
                            <div className="text-sm text-green-600 space-y-1">
                              <p>
                                Approved {doc.reviewedAt ? formatDate(doc.reviewedAt) : 'N/A'}
                                {doc.reviewer ? ` by ${doc.reviewer.firstName} ${doc.reviewer.lastName}` : ''}
                              </p>
                            </div>
                          ) : doc.reviewStatus === 'REJECTED' && doc.reviewNotes ? (
                            <p className="text-sm text-red-600">
                              Rejected: {doc.reviewNotes}
                            </p>
                          ) : doc.reviewStatus === 'RESUBMISSION_REQUIRED' ? (
                            <p className="text-sm text-yellow-600">
                              Resubmission Required{doc.reviewNotes ? `: ${doc.reviewNotes}` : ''}
                            </p>
                          ) : (
                            <p className="text-sm text-muted-foreground">
                              Status: {doc.reviewStatus}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDownloadDocument(doc.id)}
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                        <Button
                          variant={doc.reviewStatus === 'PENDING' ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => {
                            setSelectedDocument(doc);
                            setReviewModalOpen(true);
                          }}
                        >
                          <Eye className="h-4 w-4 mr-2" />
                          {doc.reviewStatus === 'PENDING' ? 'Review' : 'Preview'}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  No documents uploaded yet
                </div>
              )}
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>

          {/* Incoming Emails */}
          <IncomingEmailsCard
            workAuthId={id!}
            isOpen={openPanels.incomingEmails}
            onToggle={() => setOpenPanels(prev => ({ ...prev, incomingEmails: !prev.incomingEmails }))}
            onDocumentClick={(doc) => {
              setSelectedDocument(doc);
              setReviewModalOpen(true);
            }}
          />
        </div>

        {/* Right Column - Actions & History */}
        <div className="space-y-6">
          {/* Quick Actions - moved to top for visibility */}
          <Card>
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {!isPermanentApproval() && selectedWorkAuth.currentStatus !== 'TERMINATED' && (
                <>
                  {daysLeft > 60 && (
                    <Alert className="border-amber-500 bg-amber-50 text-amber-900 mb-2">
                      <AlertTriangle className="h-4 w-4 text-amber-600" />
                      <AlertDescription className="text-amber-800 text-xs">
                        This authorization doesn't expire for {daysLeft} days. Approval may not be needed yet.
                      </AlertDescription>
                    </Alert>
                  )}
                  <Button
                    variant="default"
                    className="w-full justify-start"
                    onClick={() => setApprovalModalOpen(true)}
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Approve Work Authorization
                  </Button>
                </>
              )}
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={handleSendReminder}
                disabled={sendingReminder}
              >
                <Mail className="h-4 w-4 mr-2" />
                {sendingReminder ? 'Sending...' : 'Send Manual Reminder'}
              </Button>
            </CardContent>
          </Card>

          {/* Notifications History */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5" />
                Notifications Sent
              </CardTitle>
            </CardHeader>
            <CardContent>
              {selectedWorkAuth.notifications && selectedWorkAuth.notifications.length > 0 ? (
                <div className="space-y-3">
                  {selectedWorkAuth.notifications.map((notif: any) => (
                    <div key={notif.id} className="border-l-2 border-blue-600 pl-3">
                      <p className="text-sm font-medium">{notif.notificationType.replace(/_/g, ' ')}</p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(notif.sentAt).toLocaleString()}
                      </p>
                      <p className="text-xs text-muted-foreground">To: {notif.recipientEmail}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No notifications sent yet</p>
              )}
            </CardContent>
          </Card>

          {/* Renewal Cycles - Hidden for permanently approved */}
          {!isPermanentApproval() && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  Renewal Cycles
                </CardTitle>
              </CardHeader>
              <CardContent>
                {selectedWorkAuth.renewalCycles && selectedWorkAuth.renewalCycles.length > 0 ? (
                  <div className="space-y-3">
                    {selectedWorkAuth.renewalCycles.map((cycle: any) => (
                      <div key={cycle.id} className="p-3 border rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <Badge variant={cycle.isActive ? 'default' : 'secondary'}>
                            {cycle.isActive ? 'Active' : 'Completed'}
                          </Badge>
                          <span className="text-xs text-muted-foreground">{cycle.cycleType}</span>
                        </div>
                        <div className="text-sm space-y-1">
                          <p>
                            Started: {new Date(cycle.cycleStartDate).toLocaleDateString()}
                          </p>
                          {cycle.documentsSubmitted && (
                            <p className="text-green-600">
                              ✓ Documents submitted
                            </p>
                          )}
                          {cycle.hrReviewComplete && (
                            <p className="text-green-600">
                              ✓ HR review complete
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No renewal cycles yet</p>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Document Review Modal */}
      <DocumentReviewModal
        document={selectedDocument}
        isOpen={reviewModalOpen}
        onClose={() => {
          setReviewModalOpen(false);
          setSelectedDocument(null);
        }}
        onReviewComplete={() => {
          // Refresh the work auth details to show updated document status
          if (id) {
            dispatch(fetchWorkAuthorizationById(id));
          }
        }}
      />

      {/* Work Authorization Approval Modal */}
      {selectedWorkAuth && (
        <WorkAuthApprovalModal
          isOpen={approvalModalOpen}
          onClose={() => setApprovalModalOpen(false)}
          onSubmit={handleApproveWorkAuth}
          workAuthId={selectedWorkAuth.id}
          employeeName={`${selectedWorkAuth.employee?.firstName} ${selectedWorkAuth.employee?.lastName}`}
        />
      )}
    </div>
  );
};

export default WorkAuthDetailPage;